import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { BAYES_PRESETS, type BayesPreset, type BayesPresetId } from '../data/bayes-presets';
import { bayesPosterior } from '../prob/conditional';
import { createFlowchart, type FlowchartHandle } from '../render/flowchart';
import { createSvgEl } from '@learnforge/demo-render';
import { createTree, type TreeHandle, type TreeNode } from '../render/tree';
import type { ProbContext, ProbKey } from '../types';
import { createToggleRow } from '@learnforge/demo-kit';

type Mode = 'tree' | 'flowchart';

function presetToTree(p: BayesPreset): TreeNode {
  return {
    labelKey: 'stat.prior',
    children: [
      {
        probability: p.prior,
        node: {
          labelKey: 'stat.posterior',
          children: [
            {
              probability: p.sensitivity,
              node: { labelKey: 'stat.likelihood' },
            },
            {
              probability: 1 - p.sensitivity,
              node: { labelKey: 'stat.likelihood' },
            },
          ],
        },
      },
      {
        probability: 1 - p.prior,
        node: {
          labelKey: 'stat.posterior',
          children: [
            {
              probability: p.falsePositiveRate,
              node: { labelKey: 'stat.likelihood' },
            },
            {
              probability: 1 - p.falsePositiveRate,
              node: { labelKey: 'stat.likelihood' },
            },
          ],
        },
      },
    ],
  };
}

export interface FlowchartValues {
  prior: number;
  sensitivity: number;
  falsePositiveRate: number;
  posterior: number;
}

export function computeFlowchartValues(input: {
  prior: number;
  sensitivity: number;
  falsePositiveRate: number;
}): FlowchartValues {
  const posterior = bayesPosterior(input.prior, input.sensitivity, input.falsePositiveRate);
  return { ...input, posterior };
}

// ============================================================================
// State
// ============================================================================

interface State {
  mode: Mode;
  presetId: BayesPresetId;
  showFormulaTrace: boolean;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, ProbKey, ProbContext> {
  return {
    labelKey: 'experiment.conditional-bayes.label',
    demoId: 'probability',
    advancedKey: 'conditional-bayes',

    initialState(): State {
      return {
        mode: 'flowchart',
        presetId: 'medical-test',
        showFormulaTrace: false,
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const stageSvg = createSvgEl('svg', { viewBox: '0 0 800 420', class: 'pb-cb-svg' });
      host.appendChild(stageSvg);
      const stageGroup = createSvgEl('g');
      stageSvg.appendChild(stageGroup);

      // Closure vars for active render handles.
      let flowchart: FlowchartHandle | null = null;
      let tree: TreeHandle | null = null;

      // ── DOM: Panel (built once) ──
      const modeLbl = document.createElement('label');
      modeLbl.textContent = ctx.i18n.t('param.submode.label');
      const modeSelect = document.createElement('select');
      for (const m of ['tree', 'flowchart'] as Mode[]) {
        const opt = document.createElement('option');
        opt.value = m;
        opt.textContent = ctx.i18n.t(
          `submode.conditional-bayes.${m}.label` as 'submode.conditional-bayes.tree.label',
        );
        modeSelect.appendChild(opt);
      }
      modeSelect.addEventListener('change', () => {
        controls.setState((s) => ({ ...s, mode: modeSelect.value as Mode }));
      });
      const modeRow = document.createElement('div');
      modeRow.className = 'pb-dropdown-row';
      modeRow.append(modeLbl, modeSelect);
      ctx.panel.appendChild(modeRow);

      const presetLbl = document.createElement('label');
      presetLbl.textContent = ctx.i18n.t('param.preset.label');
      const presetSelect = document.createElement('select');
      for (const p of BAYES_PRESETS) {
        const opt = document.createElement('option');
        opt.value = p.id;
        opt.textContent = ctx.i18n.t(p.labelKey);
        presetSelect.appendChild(opt);
      }
      presetSelect.addEventListener('change', () => {
        controls.setState((s) => ({ ...s, presetId: presetSelect.value as BayesPresetId }));
      });
      const presetRow = document.createElement('div');
      presetRow.className = 'pb-dropdown-row';
      presetRow.append(presetLbl, presetSelect);
      ctx.panel.appendChild(presetRow);

      const formulaToggle = createToggleRow({
        labelKey: 'param.show-formula-trace.label',
        i18n: ctx.i18n,
        checked: false,
        onChange: (checked) => {
          controls.setState((s) => ({ ...s, showFormulaTrace: checked }));
        },
      });
      formulaToggle.input.id = 'cb-formula-trace';
      ctx.panel.appendChild(formulaToggle.element);

      // ── RenderHandle ──
      function update(state: State): void {
        const { mode, presetId, showFormulaTrace } = state;

        // Reflect panel controls from state (without rebuilding the panel).
        modeSelect.value = mode;
        presetSelect.value = presetId;
        formulaToggle.setChecked(showFormulaTrace);

        const currentPreset = BAYES_PRESETS.find((p) => p.id === presetId)!;

        if (mode === 'flowchart') {
          // Destroy tree if active.
          if (tree !== null) {
            tree.destroy();
            tree = null;
          }
          // Rebuild flowchart when stageGroup is empty (first render or after a
          // mode/preset switch that tore down the previous flowchart).
          const values = computeFlowchartValues({
            prior: currentPreset.prior,
            sensitivity: currentPreset.sensitivity,
            falsePositiveRate: currentPreset.falsePositiveRate,
          });
          if (flowchart === null) {
            stageGroup.replaceChildren();
            flowchart = createFlowchart(stageGroup, {
              boxes: [
                {
                  id: 'prior',
                  labelKey: 'stat.prior',
                  valueKey: 'stat.prior',
                  position: { col: 0, row: 1 },
                },
                {
                  id: 'sensitivity',
                  labelKey: 'param.sensitivity.label',
                  valueKey: 'param.sensitivity.label',
                  position: { col: 1, row: 0 },
                },
                {
                  id: 'falsePositiveRate',
                  labelKey: 'param.false-positive-rate.label',
                  valueKey: 'param.false-positive-rate.label',
                  position: { col: 1, row: 2 },
                },
                {
                  id: 'posterior',
                  labelKey: 'stat.posterior',
                  valueKey: 'stat.posterior',
                  position: { col: 2, row: 1 },
                },
              ],
              edges: [
                { from: 'prior', to: 'posterior' },
                { from: 'sensitivity', to: 'posterior' },
                { from: 'falsePositiveRate', to: 'posterior' },
              ],
              values: values as unknown as Readonly<Record<string, number>>,
              i18n: ctx.i18n,
            });
          } else {
            // Preset changed but mode stayed flowchart: update values in place.
            flowchart.setValues(values as unknown as Readonly<Record<string, number>>);
          }

          // Formula trace text — always rebuild after a preset/trace toggle.
          // Remove any previous formula text then re-add if needed.
          const existingFormula = stageGroup.querySelector('.pb-cb-formula');
          if (existingFormula) {
            existingFormula.remove();
          }
          if (showFormulaTrace) {
            const txt = createSvgEl('text', {
              class: 'pb-cb-formula',
              x: 400,
              y: 400,
              'text-anchor': 'middle',
            });
            txt.textContent = `${ctx.i18n.t('formula.bayes.statement')}: P(D|+) = ${values.posterior.toFixed(4)}`;
            stageGroup.appendChild(txt);
          }
        } else {
          // Tree mode.
          // Destroy flowchart if active.
          if (flowchart !== null) {
            flowchart.destroy();
            flowchart = null;
            stageGroup.replaceChildren();
          }
          if (tree === null) {
            // createTree appends its svg element to ctx.root (== host) directly.
            tree = createTree({
              container: ctx.root,
              i18n: ctx.i18n,
              initial: presetToTree(currentPreset),
            });
          } else {
            tree.update(presetToTree(currentPreset));
          }
        }
      }

      return {
        update,
        dispose() {
          flowchart?.destroy();
          flowchart = null;
          tree?.destroy();
          tree = null;
          // Do NOT replaceChildren on ctx.root or ctx.panel — the harness owns
          // teardown of those containers. Only clean up what this render created.
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Alias used by experiments/index.ts and the test suite.
export const mountConditionalBayes = experiment.mount.bind(experiment);
